import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePericlesClient } from '../core/PericlesProvider';

import { buildEntityPathSegment } from './entityPath';

/**
 * Parameters for retrieving a single entity record.
 */
export interface UseEntityDetailParams {
  entity: string;
  id: string | number;
  enabled?: boolean;
}

/**
 * Result returned by the entity detail hook.
 */
export interface UseEntityDetailResult<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Fetches an entity record by id, exposing loading and error state.
 */
export function useEntityDetail<T = unknown>(
  params: UseEntityDetailParams
): UseEntityDetailResult<T> {
  const { baseUrl, fetcher } = usePericlesClient();
  const isEnabled = params.enabled ?? true;
  const [data, setData] = useState<T | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(undefined);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const base = useMemo(() => baseUrl.replace(/\/$/, ''), [baseUrl]);

  const url = useMemo(
    () => `${base}/entities/${buildEntityPathSegment(params.entity)}/${encodeURIComponent(String(params.id))}`,
    [base, params.entity, params.id]
  );

  useEffect(() => {
    let isCancelled = false;

    if (!isEnabled || !params.id) {
      setData(undefined);
      setIsError(false);
      setError(undefined);
      setIsLoading(false);
      return undefined;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setIsError(false);
      setError(undefined);

      try {
        const response = await fetcher(url);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const json = (await response.json()) as T;

        if (!isCancelled) {
          setData(json);
        }
      } catch (err) {
        if (!isCancelled) {
          setIsError(true);
          setError(err);
          setData(undefined);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      isCancelled = true;
    };
  }, [fetcher, isEnabled, params.id, reloadToken, url]);

  const refetch = useCallback(() => {
    setReloadToken((previous) => previous + 1);
  }, []);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
}
